import { useRef, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { ScanLine, Loader2 } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { bolehPakaiAI } from "../../lib/izinAI";

/**
 * OcrScanButton — Scan nota/receipt untuk auto-fill form Piutang/Utang.
 * Props:
 *   onExtracted: (data) => void   // { nominal, tanggal, keterangan, supplier_nama, pelanggan_nama, no_invoice, confidence }
 *   mode: "piutang" | "utang"     // affects label
 */
export default function OcrScanButton({ onExtracted, mode = "utang" }) {
  const { user, can } = useAuth();
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File terlalu besar. Max 10MB");
      return;
    }
    setBusy(true);
    const tid = toast.loading("Membaca nota via AI...");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/finance/ocr/receipt", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 45000,
      });
      const d = r.data;
      onExtracted(d);
      const conf = d.confidence || "medium";
      const emoji = conf === "high" ? "✅" : conf === "medium" ? "⚠️" : "❌";
      toast.dismiss(tid);
      toast.success(`${emoji} OCR ${conf}: ${d.nominal ? `Rp ${d.nominal.toLocaleString("id-ID")}` : "nominal tidak terbaca"} · Review & edit sebelum simpan`, { duration: 4500 });
    } catch (e) {
      toast.dismiss(tid);
      toast.error(e?.response?.data?.detail || "OCR gagal");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  // Gerbang fitur AI (brief owner 30 Agu 2026): tombolnya disembunyikan dengan
  // kunci yang SAMA dengan gerbang backend (`ai_akses.KUNCI_IZIN_AI`), supaya
  // tidak ada tombol yang ujungnya 403. Disembunyikan DI SINI, bukan di
  // FinancePiutang/FinanceUtang, supaya satu komponen = satu tempat menaruh
  // aturannya — pemakai berikutnya tidak perlu ingat menyalin gerbangnya.
  if (!bolehPakaiAI(user, can)) return null;

  return (
    <div className="flex items-center gap-2">
      <input ref={fileRef} type="file" onChange={e => handleFile(e.target.files?.[0])} accept="image/*,.pdf,.heic,.heif" className="hidden" data-testid="ocr-file-input" />
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        disabled={busy}
        data-testid="ocr-scan-btn"
        className="h-8 px-3 rounded-lg border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-medium inline-flex items-center gap-1.5 disabled:opacity-50"
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ScanLine className="w-3.5 h-3.5" />}
        {busy ? "Scanning..." : "Scan Nota (AI)"}
      </button>
      <span className="text-[10px] text-slate-400">optional · manual tetap bisa</span>
    </div>
  );
}
